/** Pile d'Échap partagée (docs/14 §2.3) : un seul appui ne ferme qu'une chose, la plus haute, dans cet ordre :
 *  fenêtre de confirmation > menu ou fenêtre ouverts > tracé > désélection > sortie du mode édition. À rang égal,
 *  la dernière inscrite l'emporte (un menu ouvert dans une fenêtre se ferme avant elle).
 *
 *  Un gestionnaire rend `false` s'il n'a rien à fermer (l'appui passe au suivant) ; sinon l'appui est consommé :
 *  `e.preventDefault()`. Tout autre écouteur d'Échap doit ignorer un appui où `e.defaultPrevented` est vrai
 *  (`isFreshEscape`). Les gestionnaires de rang `ESCAPE.modal` passent en phase de capture, avant tout le reste
 *  (comme l'alerte de confirmation jusqu'ici) ; les autres en phase de remontée sur `document`, après les champs
 *  qui traitent Échap eux-mêmes (liste déroulante ouverte, saisie annulée) et qui l'arrêtent ou le consomment. */

/** Rangs de la pile, du plus haut au plus bas. */
export const ESCAPE = {
  /** Fenêtre de confirmation (alerte modale). */
  modal: 40,
  /** Menu, liste déroulante, fenêtre ou panneau flottant ouverts. */
  menu: 30,
  /** Tracé en cours (graphique, axe). */
  drawing: 20,
  /** Objet sélectionné (sommet, LIMA). */
  selection: 10,
  /** Fiche d'objet ouverte à droite (unité, ligne, graphique, pont…) : fermée après la sélection d'édition. */
  card: 5,
  /** Mode édition du fuseau. */
  editing: 0,
} as const;

/** Gestionnaire d'Échap : `false` s'il n'a rien fermé. */
export type EscapeHandler = (e: KeyboardEvent) => boolean | void;

interface Entry {
  priority: number;
  order: number;
  handler: EscapeHandler;
}

const entries: Entry[] = [];
let order = 0;
let detach: (() => void) | null = null;

/** Appui sur Échap pas encore consommé par un autre gestionnaire. */
export function isFreshEscape(e: Pick<KeyboardEvent, "key" | "defaultPrevented">): boolean {
  return e.key === "Escape" && !e.defaultPrevented;
}

/** Passe l'appui aux gestionnaires de la phase, du plus haut au plus bas ; vrai si l'un d'eux l'a consommé. */
function dispatch(e: KeyboardEvent, capture: boolean): boolean {
  if (!isFreshEscape(e)) return false;
  const candidates = entries
    .filter((entry) => (entry.priority >= ESCAPE.modal) === capture)
    .sort((a, b) => b.priority - a.priority || b.order - a.order);
  for (const entry of candidates) {
    if (!entries.includes(entry)) continue; // retiré par un gestionnaire précédent
    if (entry.handler(e) === false) continue;
    e.preventDefault();
    return true;
  }
  return false;
}

/** Branche la pile sur une cible (le document par défaut, branché à la première inscription) ; rend de quoi la
 *  débrancher. Une seule cible à la fois. */
export function attachEscapeStack(target: EventTarget): () => void {
  detach?.();
  const onCapture = (e: Event) => dispatch(e as KeyboardEvent, true);
  const onBubble = (e: Event) => dispatch(e as KeyboardEvent, false);
  target.addEventListener("keydown", onCapture, { capture: true });
  target.addEventListener("keydown", onBubble);
  const off = () => {
    target.removeEventListener("keydown", onCapture, { capture: true });
    target.removeEventListener("keydown", onBubble);
    if (detach === off) detach = null;
  };
  detach = off;
  return off;
}

/** Inscrit un gestionnaire au rang donné ; rend sa désinscription. */
export function registerEscape(priority: number, handler: EscapeHandler): () => void {
  if (!detach && typeof document !== "undefined") attachEscapeStack(document);
  const entry: Entry = { priority, order: ++order, handler };
  entries.push(entry);
  return () => {
    const i = entries.indexOf(entry);
    if (i >= 0) entries.splice(i, 1);
  };
}

/** Rang le plus haut inscrit (`-Infinity` si la pile est vide) : une fenêtre de confirmation ouverte
 *  (`ESCAPE.modal`) coupe les raccourcis de la carte (⌘Z). */
export function topEscapePriority(): number {
  return entries.reduce((top, entry) => Math.max(top, entry.priority), -Infinity);
}

/** Nombre de gestionnaires inscrits (tests, diagnostic). */
export function escapeDepth(): number {
  return entries.length;
}

/** Vide la pile et la débranche (tests). */
export function resetEscapeStackForTests(): void {
  entries.length = 0;
  order = 0;
  detach?.();
}
